"use client"

import { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { FaInfoCircle } from "react-icons/fa"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { getDatabase, ref as dbRef, get, set as dbSet, onValue, off, push, set, } from "firebase/database"
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { db } from "../Firebase/firebase-config"

// Import components
import ProductSidebar from "./ProductSidebar"
import OrderFormStep1 from "./OrderFormStep1"
import OrderFormStep2 from "./OrderFormStep2"
import ConfirmationModal from "./ConfirmationModal"
import SuccessModal from "./SuccessModal"
import LoadingOverlay from "./LoadingOverlay"
import ErrorNotification from "./ErrorNotification"
import AccountWarningModal from "./AccountWarningModal"

// Import CSS
import "./ProductOrderForm.css"

// Constants
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const ALLOWED_FILE_TYPES = ["image/png", "image/jpg", "image/jpeg"]

const ProductOrderForm = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const product = location.state?.product || {}

  const [address, setAddress] = useState("")
  const [state, setState] = useState("")
  const [pincode, setPincode] = useState("")
  const [selectedGrade, setSelectedGrade] = useState("")
  const [selectedQuantity, setSelectedQuantity] = useState("")
  const [customQuantity, setCustomQuantity] = useState("")
  const [selectedMt, setSelectedMt] = useState("")
  const [quantities] = useState([240, 350, 480, 500, 720, 800, 1000, 1200, 2000, "Other"])
  const [mt] = useState(["25 MT", "30 MT", "35 MT", "40 MT", "45 MT"])
  const [paymentAmount, setPaymentAmount] = useState("")
  const [poFile, setPoFile] = useState(null)
  const [paymentFile, setPaymentFile] = useState(null)
  const [utrNumber, setUtrNumber] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("proof") // "proof" or "utr"
  const [userId, setUserId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({
    po: 0,
    payment: 0,
  })
  const [poUploaded, setPoUploaded] = useState(false)
  const [paymentUploaded, setPaymentUploaded] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [companyName, setCompanyName] = useState("")
  const [statesList, setStatesList] = useState([])
  const [storedAddresses, setStoredAddresses] = useState([])
  const [filteredAddresses, setFilteredAddresses] = useState([])
  const [accountDetails, setAccountDetails] = useState(null)
  const [copySuccess, setCopySuccess] = useState("")
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({})
  const [selectedSiteBalance, setSelectedSiteBalance] = useState(0)
  const [isPaymentProofRequired, setIsPaymentProofRequired] = useState(true)
  const gifUrl = "/Images/New notification.gif"
  const [showNewSiteForm, setShowNewSiteForm] = useState(false)

  // Rate functionality state variables
  const [rateRequestSent, setRateRequestSent] = useState(false)
  const [currentRate, setCurrentRate] = useState(null)
  const [rateDate, setRateDate] = useState(null)
  const [rateRequestDetails, setRateRequestDetails] = useState(null)
  const [detailsChangedAfterRate, setDetailsChangedAfterRate] = useState(false)
  const [isNegotiating, setIsNegotiating] = useState(false)
  const [userOffer, setUserOffer] = useState("")
  const [gradeRates, setGradeRates] = useState({})
  const [rateRequestedGrades, setRateRequestedGrades] = useState({})
  const [currentSiteAddress, setCurrentSiteAddress] = useState("")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [rateLoading, setRateLoading] = useState(false)
  const [selectedCode, setSelectedCode] = useState(null)
  const [showAccountWarning, setShowAccountWarning] = useState(false)

  const [documentsVerified, setDocumentsVerified] = useState(false)
  const [checkingDocuments, setCheckingDocuments] = useState(false)

  useEffect(() => {
    if (selectedCode && userId) {
      checkDocumentsForCode(selectedCode)
    }
  }, [selectedCode, userId])

  const checkDocumentsForCode = async (codeId) => {
    if (!codeId || !userId) return

    setCheckingDocuments(true)

    try {
      const storage = getStorage()
      const requiredFiles = ["gstCopy", "panCard", "workOrder", "purchaseOrder", "partnershipDeed"]

      let allDocumentsUploaded = true

      for (const field of requiredFiles) {
        try {
          const fileRef = storageRef(storage, `userDocuments/${userId}/${codeId}/${field}`)
          await getDownloadURL(fileRef)
        } catch (error) {
          // File doesn't exist
          allDocumentsUploaded = false
          break
        }
      }

      // Also check localStorage as fallback
      const documentsCompleted = localStorage.getItem(`documentsCompleted_${codeId}`) === "true"

      setDocumentsVerified(allDocumentsUploaded || documentsCompleted)
    } catch (error) {
      console.error("Error checking documents:", error)
      setDocumentsVerified(false)
    } finally {
      setCheckingDocuments(false)
    }
  }
  const getTodayOrderCount = async (userId) => {
    const database = getDatabase();
    const today = new Date();

    // Start and end of today (midnight to 23:59:59)
    const startOfDay = new Date(today.setHours(0, 0, 0, 0)).getTime();
    const endOfDay = new Date(today.setHours(23, 59, 59, 999)).getTime();

    // Query orders for this user
    const ordersRef = dbRef(database, `orders/${userId}`);
    const ordersSnapshot = await get(ordersRef);

    let count = 0;
    if (ordersSnapshot.exists()) {
      ordersSnapshot.forEach((child) => {
        const order = child.val();
        if (order.timestamp) {
          const orderTime = new Date(order.timestamp).getTime();
          if (orderTime >= startOfDay && orderTime <= endOfDay) {
            count++;
          }
        }
      });
    }

    return count;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault()

    // Check if documents are uploaded for the selected code
    if (selectedCode && !documentsVerified && !checkingDocuments) {
      // Show confirmation dialog
      const confirmRedirect = window.confirm(
        `Documents are not uploaded for code ${selectedCode}. You need to upload required documents before placing an order. Would you like to go to the documents page now?`,
      )

      if (confirmRedirect) {
        navigate("/dashboard/documents", {
          state: { codeId: selectedCode },
        })
        return
      } else {
        return // Don't proceed with the order
      }
    }

    // Original step 1 validation logic
    if (!address.trim() || !state.trim() || !selectedGrade) {
      setError("Please fill in all required fields.")
      return
    }

    if (!(selectedQuantity || selectedMt)) {
      setError("Please select either quantity or MT.")
      return
    }

    if (selectedQuantity === "Other" && (!customQuantity || customQuantity < 240 || customQuantity > 100000)) {
      setError("Custom quantity must be between 240 and 100000.")
      return
    }

    const quantityType = selectedMt ? "MT" : "bags"
    const finalQuantity = selectedMt
      ? selectedMt
      : selectedQuantity === "Other"
        ? `${customQuantity} bags`
        : `${selectedQuantity} bags`

    setFormData({
      brand: product.name,
      grade: selectedGrade,
      siteAddress: {
        address,
        state,
        pincode,
      },
      quantity: selectedQuantity === "Other" ? customQuantity : selectedQuantity,
      mt: selectedMt,
      quantityType: quantityType,
    })

    if (!accountDetails) {
      await fetchDepots(product.name)
      if (!accountDetails) {
        setShowAccountWarning(true)
      }
    }

    setCurrentStep(2)
    setError("")

    if (currentRate) {
      const quantityType = selectedMt ? "MT" : "bags"
      let totalQuantity = 0

      if (quantityType === "MT") {
        const mtNumber = Number.parseInt(selectedMt.split(" ")[0])
        if (!isNaN(mtNumber)) {
          totalQuantity = mtNumber * 20
        }
      } else {
        totalQuantity =
          selectedQuantity === "Other" ? Number.parseInt(customQuantity) : Number.parseInt(selectedQuantity || "0")
      }

      if (!isNaN(totalQuantity)) {
        const totalAmount = currentRate * totalQuantity

        if (selectedSiteBalance > 0) {
          if (totalAmount <= selectedSiteBalance) {
            setPaymentAmount("0.00")
            setIsPaymentProofRequired(false)
          } else {
            setPaymentAmount((totalAmount - selectedSiteBalance).toString())
            setIsPaymentProofRequired(true)
          }
        } else {
          setPaymentAmount(totalAmount.toString())
          setIsPaymentProofRequired(true)
        }
      }
    } else {
      if (selectedSiteBalance > 0) {
        setPaymentAmount("0.00")
        setIsPaymentProofRequired(false)
      } else {
        setPaymentAmount("")
        setIsPaymentProofRequired(true)
      }
    }

  }

  // Real-time listener for account details changes
  useEffect(() => {
    if (!userId || !address || !state || !product.name) return

    const database = getDatabase()
    const codesRef = dbRef(database, `new-code/${userId}/codes`)

    const unsubscribe = onValue(codesRef, (snapshot) => {
      if (snapshot.exists()) {
        const codesData = snapshot.val()
        let foundAccountDetails = null

        for (const [randomKey, codeData] of Object.entries(codesData)) {
          if (codeData.brand !== product.name) continue

          if (codeData.sites) {
            for (const site of Object.values(codeData.sites)) {
              if (site.address === address && site.state === state && (!pincode || site.pincode === pincode)) {
                if (codeData.depoAccount) {
                  foundAccountDetails = codeData.depoAccount
                  setSelectedCode(codeData.codeID)
                  break
                }
              }
            }
          }

          if (foundAccountDetails) break
        }

        if (foundAccountDetails) {
          setAccountDetails(foundAccountDetails)
          setShowAccountWarning(false)
        } else {
          setAccountDetails(null)
        }
      }
    })

    return () => off(codesRef)
  }, [userId, address, state, pincode, product.name])

  const handleManualRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  useEffect(() => {
    const db = getDatabase()
    const auth = getAuth()
    const connectedRef = dbRef(db, ".info/connected")

    if (!navigator.onLine) {
      setError("You are offline. Please check your internet connection.")
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setError("Session expired. Please login again.")
        setLoading(false)
        return
      }

      setUserId(user.uid)
      setError("")

      onValue(connectedRef, (snap) => {
        if (!navigator.onLine) {
          setError("You are offline. Please check your internet connection.")
          setLoading(false)
          return
        }

        if (snap.val() === false) {
          setError("Could not connect to Firebase. Please try again.")
          setLoading(false)
          return
        }

        setError("")
        fetchUserDetails(user.uid)
        fetchStates()
      })
    })

    return () => unsubscribe()
  }, [])

  const fetchUserDetails = async (uid) => {
    const db = getDatabase()
    const userRef = dbRef(db, `profileDashboard/${uid}`)

    try {
      const snapshot = await get(userRef)
      if (snapshot.exists()) {
        const userData = snapshot.val()
        setCompanyName(userData.company_name || "")
        if (userData.sites) {
          const addresses = Object.values(userData.sites)
          setStoredAddresses(addresses)
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error)
      setError("Failed to load user details.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      fetchUserCodeData()
    }
  }, [userId])

  useEffect(() => {
    if (product.name) {
      setCurrentRate(null)
      setRateDate(null)
      setRateRequestDetails(null)
      setDetailsChangedAfterRate(false)
      setRateRequestSent(false)
      setGradeRates({})
      setRateRequestedGrades({})
    }
  }, [product.name])

  useEffect(() => {
    if (product.name && userId && address && state) {
      fetchDepots(product.name)
      const siteAddressKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`

      if (siteAddressKey !== currentSiteAddress) {
        setCurrentSiteAddress(siteAddressKey)
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
        setDetailsChangedAfterRate(false)
        setRateRequestSent(false)
      }
    }
  }, [product.name, userId, address, state, pincode, selectedGrade, currentSiteAddress])

  useEffect(() => {
    if (!userId) return

    const database = getDatabase()
    const rateRef = dbRef(database, `rateRequest/${userId}`)

    const unsubscribe = onValue(rateRef, (snapshot) => {
      if (snapshot.exists()) {
        const rateData = snapshot.val()
        const rateRequests = Object.entries(rateData).map(([key, value]) => ({
          id: key,
          ...value,
        }))

        const today = new Date()
        const todayRates = rateRequests.filter((request) => {
          if (!request.rate || request.brand !== product.name) return false

          const requestDate = new Date(request.rateTimestamp || request.timestamp)
          return (
            requestDate.getDate() === today.getDate() &&
            requestDate.getMonth() === today.getMonth() &&
            requestDate.getFullYear() === today.getFullYear()
          )
        })

        const newGradeRates = {}
        const newRateRequestedGrades = {}

        todayRates.forEach((request) => {
          const siteKey = `${normalizeString(request.siteAddress?.address)}_${normalizeString(request.siteAddress?.state)}_${normalizeString(request.siteAddress?.pincode)}`
          const quantityType = request.quantityType || (request.quantity.includes("MT") ? "MT" : "bags")
          const rateKey = `${request.grade}_${quantityType}`

          if (!newGradeRates[siteKey]) {
            newGradeRates[siteKey] = {}
            newRateRequestedGrades[siteKey] = {}
          }

          newGradeRates[siteKey][rateKey] = {
            rate: request.rate,
            timestamp: request.rateTimestamp || request.timestamp,
            quantity: request.quantity,
            quantityType: quantityType,
            status: request.status || "open",
            userOffer: request.userOffer || null,
          }

          newRateRequestedGrades[siteKey][rateKey] = true
        })

        setGradeRates(newGradeRates)
        setRateRequestedGrades(newRateRequestedGrades)

        if (address && state && selectedGrade && (selectedQuantity || selectedMt)) {
          const currentSiteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`
          const quantityType = selectedMt ? "MT" : "bags"
          const rateKey = `${selectedGrade}_${quantityType}`

          if (newGradeRates[currentSiteKey] && newGradeRates[currentSiteKey][rateKey]) {
            const rateInfo = newGradeRates[currentSiteKey][rateKey]
            setCurrentRate(rateInfo.rate)
            setRateDate(rateInfo.timestamp)
            setRateRequestDetails({
              brand: product.name,
              grade: selectedGrade,
              address: address,
              state: state,
              pincode: pincode,
              quantity: rateInfo.quantity,
              quantityType: quantityType,
              status: rateInfo.status || "open",
              userOffer: rateInfo.userOffer,
            })
            setDetailsChangedAfterRate(false)
          } else {
            setCurrentRate(null)
            setRateDate(null)
            setRateRequestDetails(null)
          }
        }
      } else {
        setGradeRates({})
        setRateRequestedGrades({})
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
      }
    })

    return () => off(rateRef)
  }, [userId, product.name, address, state, pincode, selectedGrade, selectedQuantity, selectedMt])

  const fetchStates = () => {
    const states = [
      "Andaman and Nicobar Islands",
      "Andhra Pradesh",
      "Arunachal Pradesh",
      "Assam",
      "Bihar",
      "Chandigarh",
      "Chhattisgarh",
      "Dadra and Nagar Haveli and Daman and Diu",
      "Delhi",
      "Goa",
      "Gujarat",
      "Haryana",
      "Himachal Pradesh",
      "Jammu and Kashmir",
      "Jharkhand",
      "Karnataka",
      "Kerala",
      "Ladakh",
      "Lakshadweep",
      "Madhya Pradesh",
      "Maharashtra",
      "Manipur",
      "Meghalaya",
      "Mizoram",
      "Nagaland",
      "Odisha",
      "Puducherry",
      "Punjab",
      "Rajasthan",
      "Sikkim",
      "Tamil Nadu",
      "Telangana",
      "Tripura",
      "Uttar Pradesh",
      "Uttarakhand",
      "West Bengal",
    ]
    setStatesList(states)
  }

  const validateFileSize = (file) => {
    if (!file) return false

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError(`Invalid file format (${file.type}). Only PNG, JPG, JPEG are allowed.`)
      return false
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB} MB. Please upload a file under ${MAX_FILE_SIZE_MB} MB.`)
      return false
    }

    setError("")
    return true
  }

  const handlePoFileChange = (e) => {
    const file = e.target.files[0]
    if (file && validateFileSize(file)) {
      setPoFile(file)
      setPoUploaded(!!file)
    } else {
      setPoFile(null)
    }
  }

  const handlePaymentFileChange = (e) => {
    const file = e.target.files[0]
    if (file && validateFileSize(file)) {
      setPaymentFile(file)
      setPaymentUploaded(!!file)
    } else {
      setPaymentFile(null)
    }
  }

  const handleFileUpload = (file, path) => {
    return new Promise((resolve, reject) => {
      const storage = getStorage()
      const fileRef = storageRef(storage, path)
      const uploadTask = uploadBytesResumable(fileRef, file)

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          if (path.startsWith("POFiles")) {
            setUploadProgress((prev) => ({ ...prev, po: progress }))
          } else {
            setUploadProgress((prev) => ({ ...prev, payment: progress }))
          }
        },
        (error) => {
          console.error("Error uploading file:", error)
          reject(error)
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => {
              resolve(downloadURL)
            })
            .catch((error) => {
              console.error("Error getting download URL:", error)
              reject(error)
            })
        },
      )
    })
  }

  const handleGradeSelection = (grade) => {
    setSelectedGrade(grade)

    if (address && state && (selectedQuantity || selectedMt)) {
      const quantityType = selectedMt ? "MT" : "bags"
      const siteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`
      const rateKey = `${grade}_${quantityType}`

      if (gradeRates[siteKey] && gradeRates[siteKey][rateKey]) {
        const rateInfo = gradeRates[siteKey][rateKey]
        setCurrentRate(rateInfo.rate)
        setRateDate(rateInfo.timestamp)
        setRateRequestDetails({
          brand: product.name,
          grade: grade,
          address: address,
          state: state,
          pincode: pincode,
          quantity: rateInfo.quantity,
          quantityType: rateInfo.quantityType,
        })
        setDetailsChangedAfterRate(false)
        setRateRequestSent(true)
      } else {
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
        setDetailsChangedAfterRate(false)
        setRateRequestSent(false)
      }
    }
  }

  const handleAddressChange = (e) => {
    const inputText = e.target.value
    setAddress(inputText)

    const filtered = storedAddresses.filter((addr) => {
      const isSameBrand = !addr.brand || addr.brand === product.name
      if (!isSameBrand) return false

      const fullAddress = `${addr.address}, ${addr.state}, ${addr.pincode}`.toLowerCase()
      return fullAddress.includes(inputText.toLowerCase())
    })
    setFilteredAddresses(filtered)

    const exactMatch = storedAddresses.find(
      (addr) => addr.address.toLowerCase() === inputText.toLowerCase() && addr.brand === product.name,
    )

    if (exactMatch) {
      setSelectedSiteBalance(exactMatch.balance || 0)
      setState(exactMatch.state)
      setPincode(exactMatch.pincode)

      if (exactMatch.depoAccount) {
        setAccountDetails(exactMatch.depoAccount)
      } else {
        fetchDepots(product.name)
      }
    } else {
      setCurrentRate(null)
      setRateDate(null)
      setRateRequestDetails(null)
      setDetailsChangedAfterRate(false)
      setRateRequestSent(false)
    }
  }

  const handleStateChange = (e) => {
    const newState = e.target.value
    setState(newState)

    setCurrentRate(null)
    setRateDate(null)
    setRateRequestDetails(null)
    setDetailsChangedAfterRate(false)
    setRateRequestSent(false)
  }

  const handlePincodeChange = (e) => {
    const inputVal = e.target.value
    if (/^\d{0,6}$/.test(inputVal)) {
      setPincode(inputVal)

      setCurrentRate(null)
      setRateDate(null)
      setRateRequestDetails(null)
      setDetailsChangedAfterRate(false)
      setRateRequestSent(false)
    }
  }

  const handleQuantityChange = (e) => {
    setSelectedQuantity(e.target.value)
    setSelectedMt("")
    setDetailsChangedAfterRate(true)

    if (address && state && selectedGrade) {
      const quantityType = selectedMt ? "MT" : "bags"
      const siteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`
      const rateKey = `${selectedGrade}_${quantityType}`

      if (gradeRates[siteKey] && gradeRates[siteKey][rateKey]) {
        const rateInfo = gradeRates[siteKey][rateKey]
        setCurrentRate(rateInfo.rate)
        setRateDate(rateInfo.timestamp)
        setRateRequestDetails({
          brand: product.name,
          grade: selectedGrade,
          address: address,
          state: state,
          pincode: pincode,
          quantity: rateInfo.quantity,
          quantityType: rateInfo.quantityType,
        })
        setDetailsChangedAfterRate(false)
        setRateRequestSent(true)
      } else {
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
        setRateRequestSent(false)
      }
    }
  }

  const handleMtChange = (e) => {
    const mtValue = e.target.value
    setSelectedMt(mtValue)
    setSelectedQuantity("")
    setDetailsChangedAfterRate(true)

    if (address && state && selectedGrade) {
      const quantityType = selectedMt ? "MT" : "bags"
      const siteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`
      const rateKey = `${selectedGrade}_${quantityType}`

      if (gradeRates[siteKey] && gradeRates[siteKey][rateKey]) {
        const rateInfo = gradeRates[siteKey][rateKey]
        setCurrentRate(rateInfo.rate)
        setRateDate(rateInfo.timestamp)
        setRateRequestDetails({
          brand: product.name,
          grade: selectedGrade,
          address: address,
          state: state,
          pincode: pincode,
          quantity: rateInfo.quantity,
          quantityType: rateInfo.quantityType,
        })
        setDetailsChangedAfterRate(false)
        setRateRequestSent(true)
      } else {
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
        setRateRequestSent(false)
      }
    }

    if (currentRate && mtValue) {
      const mtNumber = Number.parseInt(mtValue.split(" ")[0])
      if (!isNaN(mtNumber)) {
        const totalBags = mtNumber * 20
        const totalAmount = totalBags * currentRate

        if (selectedSiteBalance > 0) {
          if (totalAmount <= selectedSiteBalance) {
            setPaymentAmount("0.00")
            setIsPaymentProofRequired(false)
          } else {
            setPaymentAmount((totalAmount - selectedSiteBalance).toString())
            setIsPaymentProofRequired(true)
          }
        } else {
          setPaymentAmount(totalAmount.toString())
          setIsPaymentProofRequired(true)
        }
      }
    }
  }

  const handleSiteSelection = (addr) => {
    setAddress(addr.address)
    setState(addr.state)
    setPincode(addr.pincode)
    setFilteredAddresses([])
    setSelectedCode(addr.code)

    if (addr.brand && product.name && addr.brand === product.name) {
      setSelectedSiteBalance(addr.balance || 0)

      if (addr.depoAccount) {
        setAccountDetails(addr.depoAccount)
      } else {
        fetchDepots(product.name)
      }
    } else {
      setSelectedSiteBalance(0)
      setAccountDetails(null)
    }

    const siteKey = `${normalizeString(addr.address)}_${normalizeString(addr.state)}_${normalizeString(addr.pincode)}`

    if (selectedGrade && (selectedQuantity || selectedMt)) {
      const quantityType = selectedMt ? "MT" : "bags"
      const rateKey = `${selectedGrade}_${quantityType}`

      if (gradeRates[siteKey] && gradeRates[siteKey][rateKey]) {
        const rateInfo = gradeRates[siteKey][rateKey]
        setCurrentRate(rateInfo.rate)
        setRateDate(rateInfo.timestamp)
        setRateRequestDetails({
          brand: product.name,
          grade: selectedGrade,
          address: addr.address,
          state: addr.state,
          pincode: addr.pincode,
          quantity: rateInfo.quantity,
          quantityType: rateInfo.quantityType,
        })
        setDetailsChangedAfterRate(false)
        setRateRequestSent(true)
      } else {
        setCurrentRate(null)
        setRateDate(null)
        setRateRequestDetails(null)
        setDetailsChangedAfterRate(false)
        setRateRequestSent(false)
      }
    }
  }

  const fetchUserCodeData = async () => {
    if (!userId) return

    try {
      const database = getDatabase()
      const userCodeRef = dbRef(database, `new-code/${userId}/codes`)
      const snapshot = await get(userCodeRef)

      if (snapshot.exists()) {
        const codesData = snapshot.val()
        const allSites = []

        Object.entries(codesData).forEach(([codeKey, codeData]) => {
          const brand = codeData.brand || "Unknown"
          const codeID = codeData.codeID || ""
          const balance = codeData.balance || 0
          const depoAccount = codeData.depoAccount || null
          const sites = codeData.sites || {}

          Object.entries(sites).forEach(([siteId, site]) => {
            allSites.push({
              ...site,
              brand,
              code: codeKey,
              codeID,
              balance,
              siteId,
              depoAccount,
              hasDepoAccount: depoAccount !== null,
            })
          })
        })

        setStoredAddresses(allSites)
      }
    } catch (error) {
      console.error("Error fetching user code data:", error)
    }
  }

  const updateSiteBalance = async (userId, selectedCode, paymentAmount) => {
    if (!userId || !selectedCode) return

    try {
      const database = getDatabase()
      const codeRef = dbRef(database, `new-code/${userId}/codes`)
      const snapshot = await get(codeRef)

      if (snapshot.exists()) {
        const codesData = snapshot.val()

        for (const [codeKey, codeData] of Object.entries(codesData)) {
          if (codeData.codeID === selectedCode) {
            const currentBalance = codeData.balance || 0
            const newBalance = currentBalance + Number(paymentAmount)

            await dbSet(dbRef(database, `new-code/${userId}/codes/${codeKey}/balance`), newBalance)
            return
          }
        }
        console.error(`Code ${selectedCode} not found in user data`)
      } else {
        console.error("No codes exist for this user")
      }
    } catch (error) {
      console.error("Error updating code balance:", error)
    }
  }

  const hasRequestedRateToday = () => {
    if (!address || !state || !selectedGrade || !(selectedQuantity || selectedMt)) return false

    const siteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`
    const quantityType = selectedMt ? "MT" : "bags"
    const rateKey = `${selectedGrade}_${quantityType}`

    return rateRequestedGrades[siteKey] && rateRequestedGrades[siteKey][rateKey]
  }

  const handleNegotiateSubmit = async () => {
    if (!userOffer || isNaN(userOffer) || Number(userOffer) <= 0) {
      alert("Please enter a valid offer amount.")
      return
    }

    if (!userId) {
      setError("User is not authenticated.")
      return
    }

    if (!rateRequestDetails) {
      setError("No rate request details available. Please request a rate first.")
      return
    }

    try {
      const database = getDatabase()
      const rateRef = dbRef(database, `rateRequest/${userId}`)
      const snapshot = await get(rateRef)

      if (!snapshot.exists()) {
        setError("No rate requests found for this user.")
        return
      }

      const rateData = snapshot.val()
      const rateRequests = Object.entries(rateData).map(([key, value]) => ({
        id: key,
        ...value,
      }))

      const matchingRequest = rateRequests.find(
        (request) =>
          request.brand === product.name &&
          request.grade === selectedGrade &&
          request.quantity === (selectedMt || (selectedQuantity === "Other" ? `${customQuantity} bags` : `${selectedQuantity} bags`)) &&
          request.quantityType === (selectedMt ? "MT" : "bags") &&
          request.siteAddress?.address === address &&
          request.siteAddress?.state === state &&
          (request.siteAddress?.pincode || "") === (pincode || "")
      )

      if (!matchingRequest) {
        setError("No matching rate request found for the selected details and quantity.")
        return
      }

      const requestRef = dbRef(database, `rateRequest/${userId}/${matchingRequest.id}`)
      await dbSet(requestRef, {
        ...matchingRequest,
        userOffer: Number(userOffer),
        status: "open",
      })

      setRateRequestDetails({
        ...rateRequestDetails,
        userOffer: Number(userOffer),
        status: "open",
      })
      setIsNegotiating(false)
      setUserOffer("")
      alert("Your offer has been submitted successfully!")
    } catch (error) {
      console.error("Error submitting offer:", error)
      setError(`Failed to submit your offer: ${error.message}`)
    }
  }

  const handleGetTodayRate = async () => {
    if (!userId) {
      setError("User is not authenticated")
      return
    }

    if (!product.name || product.name === "No brand selected") {
      setError("Please select a brand before proceeding.")
      return
    }

    if (!selectedGrade || selectedGrade === "No grade selected") {
      setError("Please select a grade before proceeding.")
      return
    }

    if (!address || !state) {
      setError("Please complete address and state fields before proceeding.")
      return
    }

    if (!(selectedQuantity || selectedMt)) {
      setError("Please select either quantity or MT before proceeding.")
      return
    }

    if (hasRequestedRateToday()) {
      setError(`You have already requested the rate for ${selectedGrade} (${selectedMt ? "MT" : "bags"}) at this site address today.`)
      return
    }

    const quantityType = selectedMt ? "MT" : "bags"
    const finalQuantity = selectedMt
      ? selectedMt
      : selectedQuantity === "Other"
        ? `${customQuantity} bags`
        : `${selectedQuantity} bags`

    setRateLoading(true)

    try {
      const database = getDatabase()
      const timestamp = getIndianTimestamp()
      const siteKey = `${normalizeString(address)}_${normalizeString(state)}_${normalizeString(pincode)}`

      const rateRequestData = {
        brand: product.name,
        grade: selectedGrade,
        quantity: finalQuantity,
        quantityType: quantityType,
        siteAddress: {
          address,
          state,
          pincode,
        },
        siteKey,
        companyName,
        timestamp,
        rate: "",
        status: "open",
      }

      const rateRequestRef = dbRef(database, `rateRequest/${userId}`)
      const newRateRequestRef = push(rateRequestRef)
      await dbSet(newRateRequestRef, rateRequestData)

      setFormData({
        brand: product.name,
        grade: selectedGrade,
        siteAddress: {
          address,
          state,
          pincode,
        },
        quantity: selectedQuantity === "Other" ? customQuantity : selectedQuantity,
        mt: selectedMt,
        quantityType: quantityType,
      })

      setRateRequestedGrades((prev) => ({
        ...prev,
        [siteKey]: {
          ...(prev[siteKey] || {}),
          [`${selectedGrade}_${quantityType}`]: true,
        },
      }))

      setDetailsChangedAfterRate(false)
      setRateRequestDetails({
        brand: product.name,
        grade: selectedGrade,
        address,
        state,
        pincode,
        quantity: finalQuantity,
        quantityType: quantityType,
      })
      setRateRequestSent(true)
      setError("")
    } catch (error) {
      setError("Error requesting rate: " + error.message)
      console.error("Error requesting rate:", error)
    } finally {
      setRateLoading(false)
    }
  }

  const handleStep2Submit = (event) => {
    event.preventDefault()

    if (!accountDetails) {
      setShowAccountWarning(true)
      return
    }

    setShowConfirmation(true)
  }

  const copyAllToClipboard = (details) => {
    const textToCopy = Object.entries(details)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n")

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    })
  }

  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value
    setPaymentAmount(value)

    if (value && value !== "0.00" && selectedSiteBalance > 0) {
      setIsPaymentProofRequired(true)
    } else if (selectedSiteBalance > 0) {
      setIsPaymentProofRequired(false)
    } else {
      setIsPaymentProofRequired(true)
    }
  }

  const handleUtrNumberChange = (e) => {
    setUtrNumber(e.target.value.toUpperCase())
  }

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method)
    if (method === "utr") {
      setPaymentFile(null)
      setPaymentUploaded(false)
    } else {
      setUtrNumber("")
    }
  }

  const handleConfirmSubmit = async (event) => {
    event.preventDefault()
    setShowConfirmation(false)
    setLoading(true)

    setTimeout(async () => {
      if (!userId) {
        setError("User is not authenticated")
        setLoading(false)
        return
      }
      const todayCount = await getTodayOrderCount(userId);

      if (todayCount >= 2) {
        setError("You can only place 2 orders per day. Please try again tomorrow.");
        setLoading(false);
        return;
      }

      if (poFile && !validateFileSize(poFile)) {
        setLoading(false)
        return
      }

      if (paymentMethod === "proof" && isPaymentProofRequired && paymentFile && !validateFileSize(paymentFile)) {
        setLoading(false)
        return
      }

      const isValidUTR = (utr) => /^[A-Za-z0-9]{12,22}$/.test(utr)

      if (paymentMethod === "utr" && isPaymentProofRequired && !isValidUTR(utrNumber)) {
        setError("Please enter a valid UTR number (12 to 22 alphanumeric characters).")
        setLoading(false)
        return
      }

      try {
        let poFileUrl = ""
        let paymentFileUrl = ""

        if (poFile) {
          const poFilePath = `POFiles/${userId}/${poFile.name}`
          poFileUrl = await handleFileUpload(poFile, poFilePath)
        }

        if (paymentMethod === "proof" && isPaymentProofRequired && paymentFile) {
          const paymentFilePath = `PaymentFiles/${userId}/${paymentFile.name}`
          paymentFileUrl = await handleFileUpload(paymentFile, paymentFilePath)
        }

        const database = getDatabase()
        const timestamp = getIndianTimestamp()
        const paymentAmountNum = Number(paymentAmount)

        const quantityType = selectedMt ? "MT" : "bags"

        let totalQuantity = 0
        if (selectedMt) {
          const mtNumber = Number.parseInt(selectedMt.split(" ")[0])
          if (!isNaN(mtNumber)) {
            totalQuantity = mtNumber
          }
        } else {
          totalQuantity =
            selectedQuantity === "Other" ? Number.parseInt(customQuantity) : Number.parseInt(selectedQuantity || "0")
        }

        const isUsingExistingBalance = paymentAmount === "0.00" && selectedSiteBalance > 0
        const poId = `PO_${Math.random().toString(36).substring(2, 8).toUpperCase()}`;


        const orderData = {
          brand: formData.brand || "",
          grade: formData.grade || "",
          siteAddress: formData.siteAddress,
          quantity: totalQuantity,
          quantityType: quantityType,
          paymentAmount: paymentAmountNum,
          PO: poId,
          poFileUrl: poFileUrl || "",
          paymentFileUrl: paymentMethod === "proof" ? paymentFileUrl : "",
          utrNumber: paymentMethod === "utr" ? utrNumber : "",
          paymentMethod: paymentMethod,
          timestamp,
          timestampMs: Date.now(),
          status: "Pending",
          companyName,
          orderHistory: [],
          usingExistingBalance: isUsingExistingBalance,
          existingBalance: selectedSiteBalance,
          rate: currentRate || "",
          code: selectedCode || null,
        }
        // ---- Store in orders node ----
        const userOrderRef = dbRef(database, `orders/${userId}`)
        const newOrderRef = push(userOrderRef)
        await dbSet(newOrderRef, orderData)
        const orderId = newOrderRef.key

        // ---- Store in ActivePO node ----

        const activePORef = dbRef(database, `ActivePO/${userId}/${poId}`)
        await dbSet(activePORef, orderData)

        if (paymentAmountNum > 0 && selectedCode) {
          await updateSiteBalance(userId, selectedCode, paymentAmountNum)
        }

        const notificationMessage = `A new cement order has been placed by ${companyName} on this site ${formData.siteAddress.address}. Please review the order details.`

        await sendNotification(userId, orderId, "New Cement Order", notificationMessage)

        localStorage.removeItem("orderFormState")
        localStorage.removeItem("documentsCompleted")

        setAddress("")
        setState("")
        setPincode("")
        setSelectedGrade("")
        setSelectedQuantity("")
        setCustomQuantity("")
        setSelectedMt("")
        setPaymentAmount("")
        setPoFile(null)
        setPaymentFile(null)
        setUtrNumber("")
        setPaymentMethod("proof")
        setError("")
        setSuccess(true)
        setCurrentStep(1)
        setSelectedSiteBalance(0)
        setIsPaymentProofRequired(true)
        setRateRequestSent(false)
        setCurrentRate(null)
        setSelectedCode(null)
      } catch (error) {
        setError("Error submitting order: " + error.message)
        console.error("Error submitting order:", error)
      } finally {
        setLoading(false)
        setUploadProgress({ po: 0, payment: 0 })
      }
    }, 100)
  }

  const sendNotification = async (userId, orderId, title, message) => {
    const notificationRef = dbRef(db, `supplier_notifications/New Orders/${userId}/${orderId}`)
    const newNotification = {
      brand: formData.brand || "",
      grade: formData.grade,
      title,
      message,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }
  
  const fetchDepots = async (brandName) => {
    if (!userId || !address || !state) return

    try {
      const database = getDatabase()
      const codesRef = dbRef(database, `new-code/${userId}/codes`)
      const codesSnapshot = await get(codesRef)

      if (!codesSnapshot.exists()) {
        setAccountDetails(null)
        return
      }

      const codesData = codesSnapshot.val()

      if (selectedCode) {
        const selectedCodeEntry = Object.entries(codesData).find(
          ([key, value]) => value.codeID === selectedCode && value.brand === brandName,
        )

        if (selectedCodeEntry) {
          const [key, codeData] = selectedCodeEntry
          if (codeData.depoAccount) {
            setAccountDetails(codeData.depoAccount)
            return
          }
        }
      }

      for (const [randomKey, codeData] of Object.entries(codesData)) {
        if (codeData.brand !== brandName) continue

        if (codeData.sites) {
          for (const site of Object.values(codeData.sites)) {
            if (site.address === address && site.state === state && (!pincode || site.pincode === pincode)) {
              if (codeData.depoAccount) {
                setAccountDetails(codeData.depoAccount)
                setSelectedCode(codeData.codeID)
                return
              }
            }
          }
        }
      }

      setAccountDetails(null)
    } catch (error) {
      console.error("Error fetching depo account:", error)
      setAccountDetails(null)
    }
  }

  const handleCancel = () => {
    setShowConfirmation(false)
  }

  
  const isStep1Complete =
    selectedGrade.length > 0 && (selectedQuantity || customQuantity || selectedMt) && address && state

  const isStep2Complete = () => {
    if (!accountDetails) return false

    const hasPaymentAmount = paymentAmount && paymentAmount !== ""

    const hasValidPayment =
      (selectedSiteBalance > 0 && paymentAmount === "0.00") ||
      (paymentAmount &&
        paymentAmount !== "0.00" &&
        (!isPaymentProofRequired ||
          (paymentMethod === "proof" && paymentFile) ||
          (paymentMethod === "utr" && /^[A-Za-z0-9]{12,22}$/.test(utrNumber))))

    return hasPaymentAmount && hasValidPayment
  }

  const orderAlarm = () => {
    alert(
      "Welcome to the Product Order Form!\n\n" +
      "Here you can:\n" +
      "- Place orders for products by filling out the required information.\n" +
      "- Ensure all fields are completed, including site address, state, grade, quantity, and payment amount.\n" +
      "- Pincode is optional but recommended for better accuracy.\n" +
      "- Get today's rate before proceeding with your order.\n" +
      "- Rates are specific to quantity (bags) or MT for the selected site and grade.\n" +
      "- Upload your Purchase Order (PO) and payment files (PNG, JPG, JPEG) with a maximum size of 5 MB each.\n" +
      "- Alternatively, you can provide UTR number instead of payment proof.\n" +
      "- If you have an existing balance, you can use it for your order or add more to your balance.\n" +
      "- The payment amount must be between ₹50,000 and ₹5,00,000.\n" +
      "- You will be prompted to confirm your details before submitting the order.\n" +
      "- For any questions or assistance, please contact support.\n" +
      "- You will receive notifications for successful submissions or errors during the process.",
    )
  }

  const handleBackToStep1 = () => {
    setCurrentStep(1)
  }

  const isGetRateButtonDisabled = () => {
    if (!isStep1Complete || rateLoading) return true
    return hasRequestedRateToday()
  }

  useEffect(() => {
    if (address && state && product.name) {
      const matchingSite = storedAddresses.find(
        (addr) =>
          addr.address.toLowerCase() === address.toLowerCase() &&
          addr.state === state &&
          (!pincode || addr.pincode === pincode) &&
          addr.brand === product.name,
      )

      if (matchingSite) {
        setSelectedSiteBalance(matchingSite.balance || 0)

        if (matchingSite.depoAccount) {
          setAccountDetails(matchingSite.depoAccount)
        } else {
          fetchDepots(product.name)
        }
      }
    }
  }, [address, state, pincode, product.name, storedAddresses])

  const normalizeString = (str) =>
    (str || "")
      .toString()
      .toLowerCase()
      .replace(/[^\w\s]|_/g, "")
      .replace(/\s+/g, "_")
      .trim()

  if (error) {
    {
      error && (
        <div className="error-overlay">
          <div className="error-box">
            <p>{error}</p>
            <button onClick={() => setError("")}>OK</button>
          </div>
        </div>
      )
    }
  }


  return (
    <div className="container-fluid pro-det-cont">
      <h1 className="page-title">
        Product Order{" "}
        <FaInfoCircle style={{ cursor: "pointer", fontSize: "1.2rem", color: "#EA8716" }} onClick={orderAlarm} />
      </h1>

      <AccountWarningModal showAccountWarning={showAccountWarning} setShowAccountWarning={setShowAccountWarning} />



      <div className="custom-step-wrapper">
        <div className={`custom-step ${currentStep === 1 ? "active" : ""}`}>
          <div className="custom-step-circle">1</div>
          <div className="custom-step-label">Product Details</div>
        </div>

        <div className="custom-step-line" />

        <div className={`custom-step ${currentStep === 2 ? "active" : ""}`}>
          <div className="custom-step-circle">2</div>
          <div className="custom-step-label">Payment & Documents</div>
        </div>

      </div>
      {selectedCode && (
        <div className={`document-status ${documentsVerified ? "verified" : "pending"}`}>
          {checkingDocuments ? (
            <span>Checking documents...</span>
          ) : documentsVerified ? (
            <span>✅ Documents verified for code {selectedCode}</span>
          ) : (
            <span>⚠️ Documents pending for code {selectedCode}</span>
          )}
        </div>
      )}

      <div className="product-detail-container">
        <ProductSidebar
          product={product}
          isGetRateButtonDisabled={isGetRateButtonDisabled}
          handleGetTodayRate={handleGetTodayRate}
          rateLoading={rateLoading}
          hasRequestedRateToday={hasRequestedRateToday}
          handleManualRefresh={handleManualRefresh}
          isRefreshing={isRefreshing}
          isStep1Complete={isStep1Complete}
          rateRequestSent={rateRequestSent}
          currentRate={currentRate}
          detailsChangedAfterRate={detailsChangedAfterRate}
          rateRequestDetails={rateRequestDetails}
          rateDate={rateDate}
          isNegotiating={isNegotiating}
          setIsNegotiating={setIsNegotiating}
          userOffer={userOffer}
          setUserOffer={setUserOffer}
          handleNegotiateSubmit={handleNegotiateSubmit}
          formatDate={formatDate}
        />

        <div className="product-content">
          {currentStep === 1 && (
            <OrderFormStep1
              handleStep1Submit={handleStep1Submit}
              address={address}
              handleAddressChange={handleAddressChange}
              storedAddresses={storedAddresses}
              product={product}
              normalizeString={normalizeString}
              state={state}
              pincode={pincode}
              setAddress={setAddress}
              setState={setState}
              setPincode={setPincode}
              setSelectedCode={setSelectedCode}
              setSelectedSiteBalance={setSelectedSiteBalance}
              setAccountDetails={setAccountDetails}
              showNewSiteForm={showNewSiteForm}
              setShowNewSiteForm={setShowNewSiteForm}
              handleSiteSelection={handleSiteSelection}
              filteredAddresses={filteredAddresses}
              statesList={statesList}
              handleStateChange={handleStateChange}
              handlePincodeChange={handlePincodeChange}
              selectedGrade={selectedGrade}
              handleGradeSelection={handleGradeSelection}
              selectedMt={selectedMt}
              handleMtChange={handleMtChange}
              mt={mt}
              selectedQuantity={selectedQuantity}
              handleQuantityChange={handleQuantityChange}
              quantities={quantities}
              customQuantity={customQuantity}
              setCustomQuantity={setCustomQuantity}
              isStep1Complete={isStep1Complete}
            />
          )}

          {currentStep === 2 && (
            <OrderFormStep2
              handleStep2Submit={handleStep2Submit}
              selectedSiteBalance={selectedSiteBalance}
              product={product}
              paymentAmount={paymentAmount}
              handlePaymentAmountChange={handlePaymentAmountChange}
              accountDetails={accountDetails}
              copyAllToClipboard={copyAllToClipboard}
              copySuccess={copySuccess}
              setShowAccountWarning={setShowAccountWarning}
              handlePoFileChange={handlePoFileChange}
              poUploaded={poUploaded}
              paymentMethod={paymentMethod}
              handlePaymentMethodChange={handlePaymentMethodChange}
              isPaymentProofRequired={isPaymentProofRequired}
              handlePaymentFileChange={handlePaymentFileChange}
              paymentUploaded={paymentUploaded}
              utrNumber={utrNumber}
              handleUtrNumberChange={handleUtrNumberChange}
              handleBackToStep1={handleBackToStep1}
              isStep2Complete={isStep2Complete}
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        showConfirmation={showConfirmation}
        formData={formData}
        currentRate={currentRate}
        paymentAmount={paymentAmount}
        selectedSiteBalance={selectedSiteBalance}
        paymentMethod={paymentMethod}
        utrNumber={utrNumber}
        handleConfirmSubmit={handleConfirmSubmit}
        handleCancel={handleCancel}
      />

      <SuccessModal success={success} />

      <ErrorNotification error={error} setError={setError} />

      <LoadingOverlay loading={loading} uploadProgress={uploadProgress} />
    </div>
  )
}

export default ProductOrderForm
